import { Dialog, H3, H5, H6 } from '@wheely/ui-kit';
import React from 'react';
import { useIntl } from 'react-intl';

import { IDocDefinition } from '@/shared/models/doc-definition';
import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import { hasUnsentFile, needsNewFile } from '@/shared/store/doc-uploader/upload-form';
import { Button } from '@src-common/view/adapters/Button';

import { CenteringContainer } from '../CenteringContainer';

import { DocUploadBlock, TOnChangeValue } from './DocUploadBlock';
import { ExpiresAtField } from './ExpiresAtField';
import { getBlockingExpiryError } from './expiry-validation';
import s from './styles.module.css';

interface IViewProps<TDocType> {
  onClickSkip: () => void;
  docDefinition?: IDocDefinition<TDocType>;
  onChangeValue: TOnChangeValue<TDocType>;
  form: IDocUploaderForm<TDocType>;
  onSubmit: () => void;
  isUploading: boolean;
  accept?: string;
  fileTypes?: string[];
  relatedInfoSlot?: React.ReactNode;
  supplementaryDocsSlot?: React.ReactNode;
  groupTitle?: React.ReactNode;
  canSubmitWithoutFile?: boolean;
  canSubmit?: boolean;
  saveHint?: React.ReactNode;
  uploadError?: string;
}

const UploadView = <TDocType extends string>({
  onClickSkip,
  docDefinition,
  onChangeValue,
  onSubmit,
  form,
  isUploading,
  accept,
  fileTypes,
  relatedInfoSlot,
  supplementaryDocsSlot,
  groupTitle,
  canSubmitWithoutFile = false,
  canSubmit = true,
  saveHint,
  uploadError,
}: IViewProps<TDocType>) => {
  const intl = useIntl();

  const isFileUnsent = hasUnsentFile(form);
  const isNewFileNeeded = needsNewFile(form);
  const DocTitle = groupTitle ? H5 : H3;

  return (
    <Dialog className={s.root} isOpen={true} onClose={isUploading ? undefined : onClickSkip}>
      <CenteringContainer dataTestId="dialog__content">
        {groupTitle && (
          <H3 capitalize={true} className={s.groupTitle} data-test-id="dialog__group-title">
            {groupTitle}
          </H3>
        )}
        <DocUploadBlock
          title={
            <DocTitle capitalize={true} data-test-id="dialog__title">
              {docDefinition?.title}
            </DocTitle>
          }
          description={docDefinition?.desc}
          form={form}
          onChangeValue={onChangeValue}
          isUploading={isUploading}
          accept={accept}
          fileTypes={fileTypes}
          error={uploadError}
          aside={
            docDefinition?.isExpires && (
              <ExpiresAtField
                form={form}
                onChangeValue={onChangeValue}
                isExpires={true}
                isUploading={isUploading}
              />
            )
          }
        >
          {relatedInfoSlot && (
            <div className={s.details} data-test-id="dialog__details">
              <H6 capitalize={true} className={s.detailsTitle} data-test-id="dialog__details-title">
                {intl.formatMessage({
                  defaultMessage: 'Document details',
                  id: 'upload_document_details_title',
                  description: 'Upload form: details section title',
                })}
              </H6>
              <div className={s.detailsDesc} data-test-id="dialog__details-description">
                {intl.formatMessage({
                  defaultMessage:
                    'Complete the details below and confirm they match your document.',
                  id: 'upload_document_related_info_description',
                  description: 'Upload form: details section description',
                })}
              </div>
              {relatedInfoSlot}
            </div>
          )}
        </DocUploadBlock>
        {supplementaryDocsSlot}
        {(!isNewFileNeeded || supplementaryDocsSlot) && (
          <div className={s.footer}>
            <Button
              data-test-id="dialog__upload-button"
              className={s.save}
              onClick={onSubmit}
              isDisabled={
                isUploading ||
                !canSubmit ||
                (!isFileUnsent && !canSubmitWithoutFile) ||
                Boolean(getBlockingExpiryError(intl, docDefinition?.isExpires, form))
              }
              isPending={isUploading}
            >
              {intl.formatMessage({
                defaultMessage: 'SAVE',
                id: 'j5G7QV',
                description: 'Upload form: button',
              })}
            </Button>
            {saveHint && (
              <span className={s.saveHint} data-test-id="dialog__save-hint">
                {saveHint}
              </span>
            )}
          </div>
        )}
      </CenteringContainer>
    </Dialog>
  );
};

export type { IViewProps };
export { UploadView };
